import $ from "jquery";
import {gsap} from "gsap/all";
import {TimingAction} from "./actions/TimingAction";
import {AlignShipAction} from "./actions/AlignShipAction";
import {StabilizeShipAction} from "./actions/StabilizeShipAction";
import {MemoryAction} from "./actions/MemoryAction";

// Maps the type strings used in the JSON sequence to their action classes.
const actionTypes = {
  "timing": TimingAction,
  "align-ship": AlignShipAction,
  "stabilize-ship": StabilizeShipAction,
  "memory": MemoryAction
};

// Runs the EDL gameplay: walks the action sequence from the JSON, mounts one
// action at a time in the action container, fills the top instructions and the
// container label, fires the stage triggers, and handles the score. Actions
// report back through onActionComplete.
export class Game {

  constructor(app) {
    this.app = app;

    // How long a success or fail state stays on screen before moving on. The
    // actions read this for their own per-step holds too.
    this.stateHoldSec = 1.5;

    // Pause between the end of an action and the swap to the next one.
    this.betweenActionsSec = 0.5;

    // Fade used when swapping the action content out and in, with the rise
    // distance of the fade in up and the stagger between the parts.
    this.swapFadeSec = 0.4;
    this.swapRiseY = 40;
    this.swapStaggerSec = 0.1;

    // When the top instructions change in the middle of an action, the action
    // waits this much more once they are back so the new text can be read.
    this.instructionsReadSec = 1;

    // Width animation of the action label when its text changes, the fade of
    // the text itself around the resize, and the pause between a mid action
    // collapse and the expand that follows.
    this.labelResizeSec = 0.4;
    this.labelFadeSec = 0.2;
    this.labelSwapPauseSec = 0.25;

    // Reading time between the content being fully shown and the gameplay
    // actually starting.
    this.startDelaySec = 1;

    // How long the ground distance gauge takes to travel to the state the
    // finished action defines, running over the swap to the next one. The
    // altitude counts over the same stretch.
    this.groundDistanceSec = 1.2;

    // Flight score letter by minimum points, checked top down. Tuned for the
    // final sequence of 16 actions (the current demo sequence tops out lower).
    this.grades = [
      {min: 16, grade: "S"},
      {min: 10, grade: "A"},
      {min: 5, grade: "B"},
      {min: 0, grade: "C"}
    ];

    this.$game = $("#screen-2 .game");
    // Two signs in the instructions: the one over them, raised by the actions
    // asking for it in the JSON, and the one under them, raised mid action by
    // the align run.
    this.$warningSign = this.$game.find(".top-instructions .warning-sign:not(.below)");
    this.$alertSign = this.$game.find(".top-instructions .warning-sign.below");
    this.$instructionsText = this.$game.find(".top-instructions .text");
    this.$instructionsSubtext = this.$game.find(".top-instructions .subtext");
    this.$actionContainer = this.$game.find(".action-container");
    this.$label = this.$actionContainer.find("> .label");
    this.$action = this.$actionContainer.find(".action");

    // Design paddings of the label, read before any tween touches them: the
    // pill collapse squeezes them to zero and the expand restores them.
    this.labelPadding = {
      left: parseFloat(this.$label.css("padding-left")) || 0,
      right: parseFloat(this.$label.css("padding-right")) || 0
    };

    this.$labelIcon = this.$label.find(".label-icon");
    if (!this.$labelIcon.length) {
      this.$labelIcon = $('<img class="label-icon" />');
      this.$label.prepend(this.$labelIcon);
    }

    this.score = 0;
    this.maxScore = 0;
    this.actionIndex = -1;
    this.currentAction = null;
    this.nextCall = null;
    this.startCall = null;
    this.swapTween = null;
    this.instructionsTween = null;
    this.labelTween = null;
    this.labelCollapsed = false;
    this.running = false;
    this.runId = 0;
  }

  getSequence() {
    return this.app.data.content.game.sequence || [];
  }

  // The pieces that fade out and in between actions, in stagger order. The
  // action container itself is revealed once by the screen intro and stays,
  // only its content swaps. The warning sign only joins while its action
  // shows it, so a hidden one never eats a slot of the stagger. Queried
  // fresh on every call so the swaps always match the current state.
  getSwapParts() {
    return this.$game.find(".top-instructions .warning-sign.shown, .top-instructions .text, .top-instructions .subtext, .action-container .action, .action-container .corner-timer");
  }

  start() {
    this.stop();

    this.score = 0;
    this.maxScore = 0;
    this.actionIndex = -1;
    this.runId++;
    this.running = true;

    // A fresh run starts back at the top of the descent.
    this.app.resetGauge();

    // The first action fades these in once its content is set.
    gsap.set(this.getSwapParts(), {autoAlpha: 0});

    // Dev shortcut: the run can open straight on a later action. The ones
    // before it are skipped and the app sets what they would have left
    // behind. See EDL.jumpIntoRun.
    const startIndex = this.app.getStartActionIndex();
    if (startIndex > 0) {
      this.actionIndex = startIndex - 1;
      this.app.jumpIntoRun(this.getSequence().slice(0, startIndex + 1));
    }

    this.nextAction();
  }

  nextAction() {
    this.actionIndex++;

    const sequence = this.getSequence();

    if (this.actionIndex >= sequence.length) {
      this.finish();
      return;
    }

    const config = sequence[this.actionIndex];
    const previousConfig = sequence[this.actionIndex - 1] || null;

    // The app gets a word in between actions (stage swap, transition
    // timeline) and resolves its promise when the next action can launch.

    // We use a runId to prevent actions resolved actions still pending when a stop or restart happened
    const runId = this.runId;

    Promise.resolve(this.app.beforeAction(config, previousConfig)).then(() => {
      if (!this.running || runId !== this.runId) return;
      this.launchAction(config);
    });
  }

  launchAction(config) {
    const ActionClass = actionTypes[config.type];
    if (!ActionClass) {
      console.warn(`Unknown action type "${config.type}", skipping`);
      this.nextAction();
      return;
    }

    // The warning sign shows for actions asking for it in the JSON, leading
    // the fade in stagger. Toggled before the swap parts are collected below.
    this.$warningSign.toggleClass("shown", !!config.warning);

    // The lower one is only ever raised from inside an action, so every action
    // opens without it.
    this.$alertSign.removeClass("shown");

    this.fillI18n(this.$instructionsText, config.instruction);
    this.fillI18n(this.$instructionsSubtext, config.subtext);
    this.setActionLabel(config.label || config.label_memorize);

    this.currentAction = new ActionClass(this, config);
    this.maxScore += this.currentAction.maxPoints;

    // The gauge takes the state the action opens on, travelling while the
    // content swaps in. An action naming no start state leaves it where the
    // previous one left it.
    this.currentAction.applyGaugeStart();

    // Content is built before the fade so it is there while fading in; the
    // gameplay itself only starts once the fade is done, except what the
    // action chooses to unlock right away in prestart.
    this.currentAction.build();
    this.currentAction.prestart();

    this.swapTween = gsap.fromTo(this.getSwapParts(), {
      autoAlpha: 0,
      y: this.swapRiseY
    }, {
      autoAlpha: 1,
      y: 0,
      duration: this.swapFadeSec,
      ease: "power2.out",
      stagger: this.swapStaggerSec,
      onComplete: () => {
        // Leave time to read the label before the gameplay kicks in.
        this.startCall = gsap.delayedCall(this.startDelaySec, () => this.currentAction.start());
      }
    });
  }

  // Called by the action once it is done (including its own end-state hold).
  onActionComplete(action) {
    this.score += action.points;
    console.log(`Action ${this.actionIndex + 1}/${this.getSequence().length} done: ${action.points}/${action.maxPoints} points, total ${this.score}/${this.maxScore}`);

    // The ship keeps falling whatever the verdict, so the gauge travels to the
    // end state of the action, missed or not. The actions that already
    // travelled there over their own duration skip this.
    action.applyGaugeEnd();

    this.nextCall = gsap.delayedCall(this.betweenActionsSec, () => {
      // The label folds away together with the leaving content, so the next
      // one expands from nothing at launch.
      this.collapseLabel();

      this.swapTween = gsap.to(this.getSwapParts(), {
        autoAlpha: 0,
        duration: this.swapFadeSec,
        ease: "power1.in",
        stagger: this.swapStaggerSec / 2,
        onComplete: () => {
          this.clearAction();
          this.nextAction();
        }
      });
    });
  }

  finish() {
    this.running = false;

    console.log(`Sequence complete, final score ${this.score}/${this.maxScore}`);

    this.updateConclusion();
    this.app.goToConclusion();
  }

  // Writes the run's result into the screen 3 score box. Learned manoeuvres is
  // what the player got right, repeated manoeuvres is what they missed and
  // would have to do again, and the letter grade comes from the points.
  updateConclusion() {
    const $conclusion = $("#screen-3 .conclusion");
    const grade = this.grades.find((entry) => this.score >= entry.min).grade;

    $conclusion.find(".flight-score .score").text(grade);
    $conclusion.find(".learned-manoeuvers .score").text(`${this.score}/${this.maxScore}`);
    $conclusion.find(".repeated-manoeuvers .score").text(`${this.maxScore - this.score}/${this.maxScore}`);
  }

  // Hard stop: kills whatever is in flight. Used by reset and before a restart.
  stop() {
    this.running = false;
    this.runId++;

    if (this.nextCall) {
      this.nextCall.kill();
      this.nextCall = null;
    }
    if (this.startCall) {
      this.startCall.kill();
      this.startCall = null;
    }
    if (this.swapTween) {
      this.swapTween.kill();
      this.swapTween = null;
    }
    if (this.instructionsTween) {
      this.instructionsTween.kill();
      this.instructionsTween = null;
    }
    if (this.labelTween) {
      this.labelTween.kill();
      this.labelTween = null;
    }

    this.clearAction();

    // Back to the small empty pill, so the box shows empty on the next reveal.
    this.fillI18n(this.$label, null);
  }

  reset() {
    this.stop();
    gsap.set(this.getSwapParts(), {autoAlpha: 0});
  }

  clearAction() {
    if (this.currentAction) {
      this.currentAction.destroy();
      this.currentAction = null;
    }

    this.$action.empty();
    this.$actionContainer.find(".corner-timer").empty();
    this.$label.removeClass("error success");
    this.setActionGlow();
  }

  // Cross-fades the action container glow to the verdict colors ("success" or
  // "error"), or back to the neutral colors with no state. The colors and the
  // fade live in CSS, this only swaps the class.
  setActionGlow(state = "") {
    this.$actionContainer.removeClass("glow-success glow-error");
    if (state) this.$actionContainer.addClass(`glow-${state}`);
  }

  // Swaps the instruction subtext while an action is running, which the align
  // run does as the ship crosses in and out of its parameters. The warning sign
  // rides along, so the line asking for a correction is the one that gets the
  // mark. The text itself changes on the spot, like the circle it answers to,
  // only the sign fades.
  setSubtext(textObj, warning = false) {
    this.fillI18n(this.$instructionsSubtext, textObj);

    const shown = this.$alertSign.hasClass("shown");
    if (!!warning === shown) return;

    this.$alertSign.toggleClass("shown", !!warning);

    gsap.fromTo(this.$alertSign, {
      autoAlpha: warning ? 0 : 1
    }, {
      autoAlpha: warning ? 1 : 0,
      duration: 0.25,
      ease: "power1.inOut"
    });
  }

  // Writes the top instructions without an action behind them, which is what
  // the pre-game note needs. No warning sign, that belongs to the actions
  // asking for one.
  setInstructions(text, subtext) {
    this.$warningSign.removeClass("shown");
    this.$alertSign.removeClass("shown");
    this.fillI18n(this.$instructionsText, text);
    this.fillI18n(this.$instructionsSubtext, subtext);
  }

  // Changes the top instructions while an action is running, with the same
  // fade out and fade in rising as between actions. Only the lines given
  // change, the others stay as they are. The warning sign stays put. Returns
  // how long the action should wait before going on: the swap and the time to
  // read it, or 0 when nothing changed.
  swapInstructions(text, subtext) {
    const $parts = $()
      .add(text ? this.$instructionsText : null)
      .add(subtext ? this.$instructionsSubtext : null);
    if (!$parts.length) return 0;

    if (this.instructionsTween) this.instructionsTween.kill();

    this.instructionsTween = gsap.timeline()
      .to($parts, {
        autoAlpha: 0,
        duration: this.swapFadeSec,
        ease: "power1.in",
        stagger: this.swapStaggerSec / 2
      })
      .call(() => {
        if (text) this.fillI18n(this.$instructionsText, text);
        if (subtext) this.fillI18n(this.$instructionsSubtext, subtext);
      })
      .fromTo($parts, {
        autoAlpha: 0,
        y: this.swapRiseY
      }, {
        autoAlpha: 1,
        y: 0,
        duration: this.swapFadeSec,
        ease: "power2.out",
        stagger: this.swapStaggerSec,
        // A fromTo sets its from values as soon as it is built, which would
        // hide the lines before the fade out above even starts.
        immediateRender: false
      });

    return this.instructionsTween.duration() + this.instructionsReadSec;
  }

  // The two lines of the top instructions, the pieces the pre-game note shows
  // and takes back off before the run starts.
  getInstructionParts() {
    return this.$game.find(".top-instructions .text, .top-instructions .subtext");
  }

  // Fills an i18n wrapper (its .fr and .en nodes) from a {fr, en} object.
  fillI18n($el, textObj) {
    const obj = textObj || {fr: "", en: ""};
    $el.find(".fr").html(obj.fr || "");
    $el.find(".en").html(obj.en || "");
  }

  // Folds the label pill away, fading its text out with it. Runs together
  // with the action content leaving, and instantly (before the container
  // reveal) so the pill is never seen empty. The next setActionLabel expands
  // from zero, guaranteeing the text is only swapped while fully collapsed.
  collapseLabel(instant = false) {
    if (this.labelTween) {
      this.labelTween.kill();
      this.labelTween = null;
    }

    const $fadeParts = this.$label.find(".i18n").add(this.$labelIcon);
    this.labelCollapsed = true;

    if (instant) {
      gsap.set($fadeParts, {autoAlpha: 0});
      gsap.set(this.$label, {
        autoAlpha: 0,
        width: 0,
        minWidth: 0,
        paddingLeft: 0,
        paddingRight: 0
      });
      return;
    }

    this.labelTween = gsap.timeline();
    this.labelTween.to($fadeParts, {
      autoAlpha: 0,
      duration: this.labelFadeSec,
      ease: "power1.in"
    }, 0);
    this.labelTween.to(this.$label, {
      width: 0,
      minWidth: 0,
      paddingLeft: 0,
      paddingRight: 0,
      duration: this.labelResizeSec,
      ease: "power2.in"
    }, 0);
    // Once folded the pill is a zero width box that would still cast its
    // box-shadow, so it is hidden outright until the next expand.
    this.labelTween.set(this.$label, {autoAlpha: 0}, this.labelResizeSec);
  }

// Updates the action container label. The optional state ("success" or
  // "error") drives the colored versions used by the memory action verdict.
  // The text only ever swaps while the pill is fully collapsed: from a
  // collapsed label (the normal case between actions) it expands right away
  // with the new text fading in; on a mid action change it collapses first,
  // holds a beat, then expands.
  setActionLabel(textObj, state = "") {
    if (this.labelTween) this.labelTween.kill();

    const $text = this.$label.find(".i18n");
    const $fadeParts = $text.add(this.$labelIcon);

    const expand = () => {
      this.labelCollapsed = false;

      this.fillI18n(this.$label, textObj);
      this.$label.removeClass("error success");
      if (state) this.$label.addClass(state);

      // Icon set (or cleared) before measuring endWidth, so the resize below
      // already accounts for its space.
      const icon = textObj && textObj.icon;
      this.$label.toggleClass("has-icon", !!icon);
      if (icon) {
        this.$labelIcon.attr("src", icon).show();
      } else {
        this.$labelIcon.hide().removeAttr("src");
      }

      // Natural width of the new text, measured with the collapse styles off.
      // Cleared again once expanded so the label can reflow freely, for
      // example on a language switch.
      gsap.set(this.$label, {clearProps: "width,minWidth,paddingLeft,paddingRight"});
      const endWidth = this.$label.outerWidth();

      this.labelTween = gsap.timeline();
      this.labelTween.fromTo(this.$label, {
        autoAlpha: 1,
        width: 0,
        minWidth: 0,
        paddingLeft: 0,
        paddingRight: 0
      }, {
        width: endWidth,
        paddingLeft: this.labelPadding.left,
        paddingRight: this.labelPadding.right,
        duration: this.labelResizeSec,
        ease: "power2.out",
        clearProps: "width,minWidth,paddingLeft,paddingRight"
      }, 0);
      this.labelTween.to($fadeParts, {
        autoAlpha: 1,
        duration: this.labelResizeSec,
        ease: "power1.out"
      }, 0);
      // The text fades in over the tail of the expansion.
      this.labelTween.to($text, {
        autoAlpha: 1,
        duration: this.labelFadeSec,
        ease: "power1.out"
      }, this.labelResizeSec - this.labelFadeSec);
    };

    if (this.labelCollapsed) {
      expand();
      return;
    }

    // Mid action change: collapse with the old text, hold a beat, expand.
    this.labelTween = gsap.timeline({onComplete: expand});
    this.labelTween.to($text, {
      autoAlpha: 0,
      duration: this.labelFadeSec,
      ease: "power1.in"
    }, 0);
    this.labelTween.to(this.$label, {
      width: 0,
      minWidth: 0,
      paddingLeft: 0,
      paddingRight: 0,
      duration: this.labelResizeSec,
      ease: "power2.in"
    }, 0);
    // Same as collapseLabel: hide the folded pill so its shadow does not sit
    // there during the pause before the new text.
    this.labelTween.set(this.$label, {autoAlpha: 0}, this.labelResizeSec);
    this.labelTween.to({}, {duration: this.labelSwapPauseSec});
  }
}


